import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AuthState,
  IgnoredError,
  ReportError,
  useAuth,
  useIsRegistered,
  useQuery,
} from '@furality/frontend-stack';
import registration from '#/api/registration.ts';
import { FancyForm, type FormStep } from '#/components/FancyForm.tsx';
import AlreadyApplied from '@/components/_voucher-request/AlreadyApplied.tsx';
import BackgroundWrapper from '@/components/_voucher-request/BackgroundWrapper.tsx';
import Closed from '@/components/_voucher-request/Closed.tsx';
import NotOpenYet from '@/components/_voucher-request/NotOpenYet.tsx';
import {
  AdditionalInfo,
  AgeVerificationCheck,
  AgeVerificationEnd,
  Agreement,
  CompatibilityEnd,
  Completed,
  Country,
  DeviceCheck,
  Email,
  HardshipCause,
  HardshipCheck,
  Introduction,
  LoginRequired,
  MemorableExperience,
  MostExciting,
  MostUsedAvatar,
  NecessitiesDifficulty,
  NoHardship,
  PaymentImpact,
  QuestionsIntro,
  ReceivingAssistance,
  Steps,
  SubmitReview,
  UsdDifficulty,
  WeNeedSomeInfo,
} from '@/components/_voucher-request/steps/index.ts';
import type VoucherRequestData from '@/components/_voucher-request/VoucherRequestData.ts';
import ErrorDisplay from '@/components/ErrorDisplay.tsx';
import { NoLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';

const STEPS: FormStep<Steps>[] = [
  {
    id: Steps.Login,
    content: LoginRequired,
    children: [],
  },
  {
    id: Steps.Introduction,
    content: Introduction,
    children: [Steps.Agreement],
  },
  {
    id: Steps.Agreement,
    content: Agreement,
    children: [Steps.WeNeedSomeInfo],
  },
  {
    id: Steps.WeNeedSomeInfo,
    content: WeNeedSomeInfo,
    children: [Steps.DoYouExperienceHardship],
  },
  {
    id: Steps.DoYouExperienceHardship,
    content: HardshipCheck,
    children: [Steps.AgeVerificationCheck, Steps.NoHardship],
  },
  {
    id: Steps.NoHardship,
    content: NoHardship,
    children: [],
  },
  {
    id: Steps.AgeVerificationCheck,
    content: AgeVerificationCheck,
    children: [Steps.DeviceCheck, Steps.AgeVerificationEnd],
  },
  {
    id: Steps.AgeVerificationEnd,
    content: AgeVerificationEnd,
    children: [],
  },
  {
    id: Steps.DeviceCheck,
    content: DeviceCheck,
    children: [Steps.QuestionsIntro, Steps.CompatibilityEnd],
  },
  {
    id: Steps.CompatibilityEnd,
    content: CompatibilityEnd,
    children: [],
  },
  {
    id: Steps.QuestionsIntro,
    content: QuestionsIntro,
    children: [Steps.Country],
  },
  {
    id: Steps.Country,
    content: Country,
    children: [Steps.MemorableExperience],
  },
  {
    id: Steps.MemorableExperience,
    content: MemorableExperience,
    children: [Steps.MostUsedAvatar],
  },
  {
    id: Steps.MostUsedAvatar,
    content: MostUsedAvatar,
    children: [Steps.MostExciting],
  },
  {
    id: Steps.MostExciting,
    content: MostExciting,
    children: [Steps.HardshipCause],
  },
  {
    id: Steps.HardshipCause,
    content: HardshipCause,
    children: [Steps.PaymentImpact],
  },
  {
    id: Steps.PaymentImpact,
    content: PaymentImpact,
    children: [Steps.NecessitiesDifficulty],
  },
  {
    id: Steps.NecessitiesDifficulty,
    content: NecessitiesDifficulty,
    children: [Steps.ReceivingAssistance],
  },
  {
    id: Steps.ReceivingAssistance,
    content: ReceivingAssistance,
    children: [Steps.UsdDifficulty],
  },
  {
    id: Steps.UsdDifficulty,
    content: UsdDifficulty,
    children: [Steps.AdditionalInfo],
  },
  {
    id: Steps.AdditionalInfo,
    content: AdditionalInfo,
    children: [Steps.Email],
  },
  {
    id: Steps.Email,
    content: Email,
    children: [Steps.SubmitReview],
  },
  {
    id: Steps.SubmitReview,
    content: SubmitReview,
    children: [Steps.Completed],
  },
  {
    id: Steps.Completed,
    content: Completed,
    children: [],
  },
];

const VoucherRequestForm = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  const featuresReady = useFlagsStatus().flagsReady;
  const enableVoucherRequest = useFlag('voucherRequests');
  const enableRegistration = useFlag('registration');

  const registered = useIsRegistered();

  const form = useForm<VoucherRequestData>({
    defaultValues: {
      device: '',
      countryOfResidence: '',
      memorableExperience: '',
      mostUsedAvatar: '',
      mostExciting: '',
      circumstances: '',
      purchaseImpact: '',
      affordingNecessities: '',
      receivingAssistance: '',
      difficultyInUsd: '',
      otherConsiderations: '',
      email: '',
    },
  });

  const { t, ready: translationReady } = useTranslation('voucher_request');

  const alreadyApplied = useQuery({
    queryKey: [QUERY_KEYS.VoucherRequests.Applied],
    queryFn: async () => {
      const res = await registration.value.fhpGetFhpRequests();

      switch (res.status) {
        case 200:
          return res.data.exists;
        case 401:
          throw new IgnoredError(res);
        default:
          throw new ReportError(res);
      }
    },
    enabled: auth.state === 'loggedIn',
  });

  if (registered.registered) {
    void navigate({ to: '/', replace: true });
    return <PageLoading />;
  }

  if (auth.state === AuthState.Error) {
    return (
      <div className="flex-center h-100 flex-grow-1 min-vh-100">
        <ErrorDisplay title={t('sign_in_error')} error={auth.error} />
      </div>
    );
  }

  if (
    !featuresReady ||
    auth.state === AuthState.Loading ||
    alreadyApplied.isLoading ||
    !translationReady
  ) {
    return <PageLoading />;
  }

  if (enableRegistration && !enableVoucherRequest) {
    return <NotOpenYet />;
  }

  if (!enableRegistration || !enableVoucherRequest) {
    return <Closed />;
  }

  if (alreadyApplied.data) {
    return <AlreadyApplied />;
  }

  return (
    <BackgroundWrapper>
      <FormProvider {...form}>
        <FancyForm
          steps={STEPS}
          origin={auth.state === AuthState.LoggedIn ? Steps.Introduction : Steps.Login}
        />
      </FormProvider>
    </BackgroundWrapper>
  );
};

export const Route = createFileRoute('/voucher-request')({
  component: () => (
    <NoLayout title="shared.navigation.voucher-request">
      <VoucherRequestForm />
    </NoLayout>
  ),
});
